import React, { useEffect, useState } from 'react';
import { omit } from 'lodash';
import uuid from 'uuid';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useUser2 } from 'contexts/user2';
import {
    Box,
    Button,
    FileUpload,
    IconButton,
    ResourceChip,
    ResponsiveDialog,
    Stack,
    Tabs,
    Text,
    TextInput,
} from 'shell/components';
import { TYPES } from 'utils/constants';
import { uploadFile } from 'utils/file';

const UploadResourcesModal = ({ device, isOpen, isSubmitting, submitResources, onClose }) => {
    const { auth } = useAuth();
    const { course, currentGroup } = useUser2();

    const [tab, setTab] = useState('file');
    const [files, setFiles] = useState([]);
    const [link, setLink] = useState({ url: '', name: '' });

    const handleSelect = ({ key }, file) => {
        switch (key) {
            case 'delete':
                setFiles((current) => current.filter((currentFile) => currentFile.id !== file.id));
                break;
        }
    };

    const handleChange = async (files) => {
        const timestamp = new Date().getTime();
        const fileArray = Array.from(files);
        const filesToUpload = fileArray.map((file) => ({
            id: uuid.v4(),
            active: true,
            owner_account_id: auth.owner_account_id,
            course_id: course.id,
            ...(currentGroup && {
                group_id: currentGroup.id,
            }),
            created_on: timestamp,
            modified_on: timestamp,
            data: {
                name: file.name,
                size: file.size,
                type: file.type,
            },
            type: TYPES.TASK,
            public: true,
            loading: true,
        }));
        setFiles((current) => [...current, ...filesToUpload]);
        await Promise.all(
            filesToUpload.map(async (file, index) => {
                uploadFile(
                    files[index],
                    () => {},
                    (data) => {
                        setFiles((current) => {
                            return current.map((currentFile) => {
                                if (currentFile.id === file.id) {
                                    return {
                                        ...omit(currentFile, ['loading']),
                                        data: {
                                            ...currentFile.data,
                                            ...data,
                                        },
                                    };
                                } else return currentFile;
                            });
                        });
                    },
                    (e) => {
                        console.error('File upload failed.', e);
                    },
                    auth.owner_account_id
                );
            })
        );
    };

    const renderUploadFile = () => {
        return files.length ? (
            <Stack alignItems={'flex-end'} spacing={2}>
                {files.map((file) => (
                    <CustomResourceChip
                        key={file.id}
                        options={[{ key: 'delete', value: 'Delete' }]}
                        resource={file}
                        size={'medium'}
                        type={'file'}
                        onSelect={(option) => handleSelect(option, file)}
                    />
                ))}
                <Button
                    data-cy={'upload-button'}
                    disabled={files.some((file) => file.loading)}
                    loading={isSubmitting}
                    onClick={() =>
                        submitResources(
                            files.map((file) => file.data),
                            link
                        )
                    }
                >
                    Upload File
                </Button>
            </Stack>
        ) : (
            <FileUpload onChange={handleChange} />
        );
    };

    const renderAddLink = () => {
        return (
            <Stack alignItems={'flex-end'} spacing={4}>
                <TextInput
                    label={'URL'}
                    placeholder={'e.g. www.google.com'}
                    value={link.url}
                    onChange={(e) => setLink((current) => ({ ...current, url: e.target.value }))}
                />
                <TextInput
                    label={'Link Name'}
                    placeholder={'e.g. "Link to Google"'}
                    value={link.name}
                    onChange={(e) => setLink((current) => ({ ...current, name: e.target.value }))}
                />
                <Button
                    disabled={!link.url || !link.name}
                    loading={isSubmitting}
                    onClick={() =>
                        submitResources(
                            files.map((file) => file.data),
                            link
                        )
                    }
                >
                    Save Link
                </Button>
            </Stack>
        );
    };

    const tabList = [
        {
            key: 'file',
            value: 'Upload File',
            content: <CustomBox device={device}>{renderUploadFile()}</CustomBox>,
        },
        {
            key: 'link',
            value: 'Add Link',
            content: <CustomBox device={device}>{renderAddLink()}</CustomBox>,
        },
    ];

    useEffect(() => {
        if (!isOpen) {
            setTimeout(() => {
                setTab(tabList[0].key);
                setFiles([]);
                setLink({ url: '', name: '' });
            }, 150);
        }
    }, [isOpen]);

    return (
        <ResponsiveDialog open={isOpen} onClose={onClose}>
            <Stack sx={{ minWidth: '350px' }} spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'}>Upload a Resource</Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                <Tabs aria-label={'select resource to upload'} options={tabList} selected={tab} onChange={setTab} />
            </Stack>
        </ResponsiveDialog>
    );
};

const CustomBox = styled(Box)(({ device }) => {
    return {
        height: '350px',
        ...(device === 'mobile' && {
            height: 'calc(100% - 86px)',
        }),
        ...(device === 'tablet' && {
            width: '350px',
        }),
        ...(device === 'desktop' && {
            width: '350px',
        }),
    };
});

const CustomResourceChip = styled(ResourceChip)(() => {
    return {
        width: '100%',
        maxWidth: 'unset',
        '& > div': {
            width: '100%',
        },
    };
});

export default UploadResourcesModal;
